export interface ShipmentReferenceRow {
  code_1?: string | null;
  description_1?: string | null;
}

export interface ShipmentSelectOption {
  value: string;
  label: string;
}

const DISALLOWED_SHIPMENT_PLACEHOLDERS = new Set([
  "n/a",
  "not available",
  "not applicable",
  "null",
]);

export const normalizeShipmentNaValue = (value?: string | null): string => {
  const trimmed = String(value || "").trim();
  return trimmed.toLowerCase() === "na" ? "na" : trimmed;
};

export const hasDisallowedShipmentPlaceholder = (
  value?: string | null,
  allowNa = false
): boolean => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return false;
  if (normalized === "na") return !allowNa;
  return DISALLOWED_SHIPMENT_PLACEHOLDERS.has(normalized);
};

export const normalizeShipmentReferenceCode = (value?: string | null): string => {
  if (!value) return "";

  const normalized = String(value).trim();
  if (normalized.startsWith("[")) return normalized;

  const parenthesizedCode = normalized.match(/\(([A-Za-z0-9_-]+)\)$/);
  return parenthesizedCode?.[1] || normalized;
};

export const getShipmentCountryCodeForState = (
  stateValue?: string | null
): string => {
  const stateCode = normalizeShipmentReferenceCode(stateValue);
  if (!stateCode) return "";
  if (stateCode === "00" || stateCode === "00-00") return "00";
  return stateCode.split("-", 1)[0];
};

export const filterShipmentCountryOptionsByState = (
  stateValue: string | null | undefined,
  countryOptions: ShipmentSelectOption[]
): ShipmentSelectOption[] => {
  const countryCode = getShipmentCountryCodeForState(stateValue);
  if (!countryCode) return countryOptions;

  return countryOptions.filter(
    (option) => normalizeShipmentReferenceCode(option.value) === countryCode
  );
};

export const buildShipmentStateOptions = (
  countryValue: string,
  rows: ShipmentReferenceRow[]
): ShipmentSelectOption[] => {
  const options = rows
    .filter((row) => Boolean(row?.code_1))
    .map((row) => ({
      value: String(row.code_1),
      label: `${row.description_1 || ""} (${row.code_1})`.trim(),
    }));

  if (normalizeShipmentReferenceCode(countryValue) !== "00") {
    return options;
  }

  // DAT 501 defines 00 as the only dummy state for dummy country 00. Do not
  // expose the legacy 00-00 "Not applicable" option in this combination.
  const dummyState = options.find((option) => option.value === "00");
  return [dummyState || { value: "00", label: "Dummy (00)" }];
};

export const normalizeShipmentStateCode = (
  countryValue: string,
  stateValue?: string | null
): string => {
  const stateCode = String(stateValue || "").trim();
  return normalizeShipmentReferenceCode(countryValue) === "00" &&
    stateCode === "00-00"
    ? "00"
    : stateCode;
};
